'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { BiCalendar, BiChevronLeft, BiChevronRight } from 'react-icons/bi';

interface DateFilterInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
}

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const parseDateValue = (value: string): Date | null => {
  if (!value) return null;

  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;

  const parsed = new Date(year, month - 1, day);
  if (Number.isNaN(parsed.getTime())) return null;

  return parsed;
};

const toDateValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const monthStart = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);

const clampToMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);

const isBeforeDay = (candidate: Date, minDate: Date | null) => {
  if (!minDate) return false;
  return candidate < new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
};

const isAfterDay = (candidate: Date, maxDate: Date | null) => {
  if (!maxDate) return false;
  return candidate > new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
};

export function DateFilterInput({ label, value, onChange, min, max }: DateFilterInputProps) {
  const selectedDate = useMemo(() => parseDateValue(value), [value]);
  const minDate = useMemo(() => parseDateValue(min ?? ''), [min]);
  const maxDate = useMemo(() => parseDateValue(max ?? ''), [max]);

  const [isOpen, setIsOpen] = useState(false);
  const [monthCursor, setMonthCursor] = useState<Date>(() => clampToMonth(selectedDate ?? new Date()));
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setMonthCursor(clampToMonth(selectedDate ?? new Date()));
  }, [selectedDate]);

  useEffect(() => {
    const closeOnOutside = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', closeOnOutside);
    document.addEventListener('touchstart', closeOnOutside);

    return () => {
      document.removeEventListener('mousedown', closeOnOutside);
      document.removeEventListener('touchstart', closeOnOutside);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isOpen]);

  const monthLabel = monthCursor.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  const daysInCurrentMonth = new Date(
    monthCursor.getFullYear(),
    monthCursor.getMonth() + 1,
    0
  ).getDate();
  const firstWeekday = monthStart(monthCursor).getDay();

  const dayCells: Array<Date | null> = [
    ...Array.from({ length: firstWeekday }, () => null),
    ...Array.from({ length: daysInCurrentMonth }, (_, index) =>
      new Date(monthCursor.getFullYear(), monthCursor.getMonth(), index + 1)
    ),
  ];

  const displayValue = selectedDate
    ? selectedDate.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      })
    : 'mm/dd/yyyy';

  const navigateMonth = (direction: -1 | 1) => {
    setMonthCursor((current) =>
      new Date(current.getFullYear(), current.getMonth() + direction, 1)
    );
  };

  const today = new Date();
  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const isTodayDisabled = isBeforeDay(todayDate, minDate) || isAfterDay(todayDate, maxDate);

  return (
    <div className="flex w-full flex-col gap-1 sm:w-auto sm:min-w-40">
      <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-black">{label}</label>

      <div className="relative" ref={containerRef}>
        <button
          type="button"
          onClick={() => setIsOpen((current) => !current)}
          className="w-full cursor-pointer rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 pr-10 text-left text-sm text-slate-700 shadow-sm transition-colors hover:border-blue-200 focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-100"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
        >
          <span className={selectedDate ? 'text-slate-700' : 'text-slate-400'}>{displayValue}</span>
        </button>

        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
          <BiCalendar size={16} />
        </span>

        {isOpen && (
          <div className="absolute left-0 right-0 z-50 mt-1 rounded-xl border border-slate-200 bg-white p-3 shadow-lg sm:w-72 sm:max-w-[calc(100vw-2rem)] sm:left-auto sm:right-0">
            <div className="mb-3 flex items-center justify-between">
              <button
                type="button"
                onClick={() => navigateMonth(-1)}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
                aria-label="Previous month"
              >
                <BiChevronLeft size={18} />
              </button>

              <div className="text-sm font-semibold text-[#0f1f4b]">{monthLabel}</div>

              <button
                type="button"
                onClick={() => navigateMonth(1)}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
                aria-label="Next month"
              >
                <BiChevronRight size={18} />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-semibold uppercase text-slate-400">
              {WEEKDAY_LABELS.map((day) => (
                <div key={day}>{day}</div>
              ))}
            </div>

            <div className="mt-1 grid grid-cols-7 gap-1">
              {dayCells.map((day, index) => {
                if (!day) {
                  return <div key={`empty-${index}`} className="h-8" />;
                }

                const isDisabled = isBeforeDay(day, minDate) || isAfterDay(day, maxDate);
                const selected = selectedDate ? isSameDay(day, selectedDate) : false;
                const currentDay = isSameDay(day, todayDate);

                return (
                  <button
                    key={toDateValue(day)}
                    type="button"
                    onClick={() => {
                      onChange(toDateValue(day));
                      setIsOpen(false);
                    }}
                    disabled={isDisabled}
                    className={`h-8 rounded-lg text-xs font-medium transition-colors ${
                      selected
                        ? 'bg-blue-600 text-white'
                        : currentDay
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-slate-700 hover:bg-slate-100'
                    } ${isDisabled ? 'cursor-not-allowed bg-slate-50 text-slate-300' : 'cursor-pointer'}`}
                  >
                    {day.getDate()}
                  </button>
                );
              })}
            </div>

            <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
              <button
                type="button"
                onClick={() => {
                  onChange('');
                  setIsOpen(false);
                }}
                className="cursor-pointer text-xs font-semibold text-slate-500 transition-colors hover:text-slate-700"
              >
                Clear
              </button>

              <button
                type="button"
                disabled={isTodayDisabled}
                onClick={() => {
                  onChange(toDateValue(todayDate));
                  setIsOpen(false);
                }}
                className="cursor-pointer text-xs font-semibold text-blue-600 transition-colors hover:text-blue-700 disabled:cursor-not-allowed disabled:text-slate-300"
              >
                Today
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
